import React from 'react';
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer, TableRow, TextField, RadioGroup,
  FormControlLabel, Radio, Paper, Typography, IconButton, Tooltip, Stack, Button, Checkbox,
  Select, MenuItem, FormControl, InputLabel, InputAdornment, TableHead
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

const ROWS = [
  'Lens',
  'Vitreous',
  'Optic Nerve Head', // heading row
  'C/D Ratio', 'Depth', 'Margin', 'Color', 'LC',
  'VP', 'A/V Ratio', 'BV Cross', 'Course', 'Macula', 'Reflex', 'Periphery'
];

export function getDefaultInternValues() {
  const init = {};
  ROWS.forEach(r => {
    if (r === 'Optic Nerve Head') return; // heading only
    if (r === 'LC') init[r] = { od: '', os: '' }; // will store 'Seen' | 'Not Seen'
    else init[r] = { od: '', os: '' };
  });
  return {
    findings: init,
    diagrams: {
      eye: { od: [], os: [] }, // side-view eyes (top row)
      lens: { od: [], os: [] }, // bottom-left panel
      disc: { od: [], os: [] },  // bottom-right panel
    },
    methods: {
      direct: false,
      bio: false,
      other: false,
      methodsText: '',
    },
    dfe: '',
    time: '',
    declinedRisks: false,
    notes: '',
  };
}

function CombinedInternCanvas({ value, onChange }) {
  const canvasRef = React.useRef(null);
  const [editing, setEditing] = React.useState(true);
  const actionStack = React.useRef([]); // track last added point for undo

  const getLayout = (w, h) => {
    // Define rectangles for 4 panels: top eyes (OD, OS), bottom lens (OD/OS) and disc (OD/OS)
    const pad = 12;
    const topH = Math.floor(h * 0.45);
    const botH = h - topH - pad * 2;
    const colW = Math.floor((w - pad * 3) / 2);
    const row1Y = pad;
    const row2Y = row1Y + topH + pad;
    return {
      eyeOD: { x: pad, y: row1Y, w: colW, h: topH },
      eyeOS: { x: pad * 2 + colW, y: row1Y, w: colW, h: topH },
      lensPanel: { x: pad, y: row2Y, w: colW, h: botH },
      discPanel: { x: pad * 2 + colW, y: row2Y, w: colW, h: botH },
    };
  };

  const drawEyeSide = (ctx, rect, side /* 'od'|'os' */) => {
    const { x, y, w, h } = rect;
    // Two large circles side-by-side (we'll draw a single large circle for the eye outline)
    const r = Math.min(w, h) * 0.35;
    const cx = x + w / 2;
    const cy = y + h / 2;
    // Eye globe
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = '#555'; ctx.lineWidth = 2; ctx.stroke();
    // Pupil near edge to depict side view: OD pupil to temporal (right), OS pupil to temporal (left)
    const pupilOffset = r * 0.55;
    const px = side === 'od' ? cx + pupilOffset : cx - pupilOffset;
    const py = cy;
    ctx.beginPath(); ctx.arc(px, py, r * 0.15, 0, Math.PI * 2); ctx.fillStyle = '#333'; ctx.fill();
    // Label
    ctx.fillStyle = '#666'; ctx.font = '12px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(side.toUpperCase(), cx, y + 14);
  };

  const drawConcentric = (ctx, centerX, centerY, baseR, isDisc) => {
    // Draw two concentric circles; for disc draw smaller inner to represent cup
    ctx.beginPath(); ctx.arc(centerX, centerY, baseR, 0, Math.PI * 2); ctx.strokeStyle = '#555'; ctx.lineWidth = 2; ctx.stroke();
    ctx.beginPath(); ctx.arc(centerX, centerY, isDisc ? baseR * 0.55 : baseR * 0.75, 0, Math.PI * 2); ctx.strokeStyle = '#999'; ctx.lineWidth = 1.5; ctx.stroke();
  };

  const drawBottomPanel = (ctx, rect, label, points) => {
    const { x, y, w, h } = rect;
    // Title
    ctx.fillStyle = '#666'; ctx.font = '12px sans-serif'; ctx.textAlign = 'left';
    ctx.fillText(label, x + 4, y + 14);
    // Two side-by-side OD/OS centers
    const innerPad = 20;
    const cw = (w - innerPad * 3) / 2; // panel width for each eye
    const ch = h - innerPad * 2;
    const odRect = { x: x + innerPad, y: y + innerPad, w: cw, h: ch };
    const osRect = { x: x + innerPad * 2 + cw, y: y + innerPad, w: cw, h: ch };

    const drawEyeSet = (rct, side, isDisc) => {
      const cx = rct.x + rct.w / 2; const cy = rct.y + rct.h / 2; const r = Math.min(rct.w, rct.h) * 0.35;
      drawConcentric(ctx, cx, cy, r, isDisc);
      ctx.fillStyle = '#666'; ctx.textAlign = 'center'; ctx.fillText(side.toUpperCase(), cx, rct.y + 12);
      // Plot points
      const arr = points[side] || [];
      ctx.fillStyle = '#1976d2';
      arr.forEach(p => {
        const px = rct.x + p.x * rct.w;
        const py = rct.y + p.y * rct.h;
        ctx.beginPath(); ctx.arc(px, py, 4, 0, Math.PI * 2); ctx.fill();
      });
      return rct;
    };
    const odR = drawEyeSet(odRect, 'od', label === 'Disc');
    const osR = drawEyeSet(osRect, 'os', label === 'Disc');
    return { odR, osR };
  };

  const drawAll = () => {
    const c = canvasRef.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr; c.height = rect.height * dpr;
    const ctx = c.getContext('2d'); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    const layout = getLayout(rect.width, rect.height);
    // Top eyes
    drawEyeSide(ctx, layout.eyeOD, 'od');
    drawEyeSide(ctx, layout.eyeOS, 'os');
    // Plot top eye points
    const plotEyePts = (rct, side) => {
      const arr = value?.eye?.[side] || [];
      ctx.fillStyle = '#1976d2';
      arr.forEach(p => {
        const px = rct.x + p.x * rct.w;
        const py = rct.y + p.y * rct.h;
        ctx.beginPath(); ctx.arc(px, py, 4, 0, Math.PI * 2); ctx.fill();
      });
    };
    plotEyePts(layout.eyeOD, 'od');
    plotEyePts(layout.eyeOS, 'os');
    // Bottom panels
    const lensRects = drawBottomPanel(ctx, layout.lensPanel, 'Lens', value?.lens || { od: [], os: [] });
    const discRects = drawBottomPanel(ctx, layout.discPanel, 'Disc', value?.disc || { od: [], os: [] });
    // Return hit regions for click mapping
    return { layout, lensRects, discRects };
  };

  React.useEffect(() => { drawAll(); });

  const mapClickToArea = (clientX, clientY) => {
    const c = canvasRef.current; if (!c) return null;
    const rect = c.getBoundingClientRect();
    const x = clientX - rect.left; const y = clientY - rect.top;
    // We need up-to-date regions; redraw and get rects
    const regions = drawAll(); if (!regions) return null;
    const { layout, lensRects, discRects } = regions;
    const within = (r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
    if (within(layout.eyeOD)) return { area: 'eye', side: 'od', rect: layout.eyeOD };
    if (within(layout.eyeOS)) return { area: 'eye', side: 'os', rect: layout.eyeOS };
    if (within(lensRects.odR)) return { area: 'lens', side: 'od', rect: lensRects.odR };
    if (within(lensRects.osR)) return { area: 'lens', side: 'os', rect: lensRects.osR };
    if (within(discRects.odR)) return { area: 'disc', side: 'od', rect: discRects.odR };
    if (within(discRects.osR)) return { area: 'disc', side: 'os', rect: discRects.osR };
    return null;
  };

  const handleClick = (e) => {
    if (!editing) return;
    const mapped = mapClickToArea(e.clientX, e.clientY);
    if (!mapped) return;
    const { area, side, rect } = mapped;
    // normalize to sub-rect
    const nx = (e.clientX - (canvasRef.current.getBoundingClientRect().left + rect.x)) / rect.w;
    const ny = (e.clientY - (canvasRef.current.getBoundingClientRect().top + rect.y)) / rect.h;
    const next = {
      eye: { od: [...(value?.eye?.od || [])], os: [...(value?.eye?.os || [])] },
      lens: { od: [...(value?.lens?.od || [])], os: [...(value?.lens?.os || [])] },
      disc: { od: [...(value?.disc?.od || [])], os: [...(value?.disc?.os || [])] },
    };
    next[area][side].push({ x: nx, y: ny });
    actionStack.current.push({ area, side });
    onChange?.(next);
  };

  const undoLast = () => {
    const last = actionStack.current.pop();
    if (!last) return;
    const next = {
      eye: { od: [...(value?.eye?.od || [])], os: [...(value?.eye?.os || [])] },
      lens: { od: [...(value?.lens?.od || [])], os: [...(value?.lens?.os || [])] },
      disc: { od: [...(value?.disc?.od || [])], os: [...(value?.disc?.os || [])] },
    };
    const arr = next[last.area][last.side];
    arr.pop();
    onChange?.(next);
  };

  const clearAll = () => {
    actionStack.current = [];
    onChange?.({ eye: { od: [], os: [] }, lens: { od: [], os: [] }, disc: { od: [], os: [] } });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Internal Schematics</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={editing ? 'Disable Edit' : 'Enable Edit'}>
            <IconButton size="small" onClick={() => setEditing(e => !e)} color={editing ? 'primary' : 'default'}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save as PNG">
            <IconButton size="small" onClick={() => { const c = canvasRef.current; if (!c) return; const a = document.createElement('a'); a.download = 'intern-canvas.png'; a.href = c.toDataURL('image/png'); a.click(); }}>
              <SaveAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Undo last">
            <Button size="small" onClick={undoLast} variant="text">
              Undo
            </Button>
          </Tooltip>
          <Tooltip title="Clear all">
            <IconButton size="small" onClick={clearAll}>
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, height: 400 }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block', cursor: editing ? 'crosshair' : 'default' }} onClick={handleClick} />
      </Box>
    </Paper>
  );
}

export default function InternTab({ value, onChange }) {
  const v = value || getDefaultInternValues();

  const setFind = (row, side, text) => {
    const findings = { ...v.findings, [row]: { ...v.findings[row], [side]: text } };
    onChange?.({ ...v, findings });
  };

  const setLC = (side, val) => {
    const findings = { ...v.findings, LC: { ...v.findings.LC, [side]: val } };
    onChange?.({ ...v, findings });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Column 1: Table */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Internal Exam</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell align="center">OD</TableCell>
                    <TableCell align="center">OS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ROWS.map((r, idx) => {
                    if (r === 'Optic Nerve Head') {
                      return (
                        <TableRow key={`h-${idx}`}>
                          <TableCell colSpan={3} sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>Optic Nerve Head</TableCell>
                        </TableRow>
                      );
                    }
                    if (r === 'LC') {
                      return (
                        <TableRow key={r}>
                          <TableCell sx={{ width: 160, fontWeight: 600 }}>{r}</TableCell>
                          <TableCell >
                            <RadioGroup row value={v.findings.LC.od || ''} onChange={(e) => setLC('od', e.target.value)}>
                              <FormControlLabel value="Seen" control={<Radio size="small" />} label="Seen" />
                              <FormControlLabel value="Not Seen" control={<Radio size="small" />} label="Not Seen" />
                            </RadioGroup>
                          </TableCell>
                          <TableCell >
                            <RadioGroup row value={v.findings.LC.os || ''} onChange={(e) => setLC('os', e.target.value)}>
                              <FormControlLabel value="Seen" control={<Radio size="small" />} label="Seen" />
                              <FormControlLabel value="Not Seen" control={<Radio size="small" />} label="Not Seen" />
                            </RadioGroup>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const center = ['C/D Ratio','Depth','Margin','Color'].includes(r);
                    return (
                      <TableRow key={r}>
                        <TableCell sx={{ width: 160, fontWeight: 600 }} align={center ? 'center' : 'left'}>{r}</TableCell>
                        <TableCell >
                          <TextField size="small" value={v.findings[r].od} onChange={(e) => setFind(r, 'od', e.target.value)} inputProps={center ? { style: { textAlign: 'center' } } : undefined} />
                        </TableCell>
                        <TableCell >
                          <TextField size="small" value={v.findings[r].os} onChange={(e) => setFind(r, 'os', e.target.value)} inputProps={center ? { style: { textAlign: 'center' } } : undefined} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Column 2: Single Combined Canvas */}
        <Grid item xs={12} md={6}>
          <CombinedInternCanvas value={v.diagrams} onChange={(diagrams) => onChange?.({ ...v, diagrams })} />
          
          {/* Additional Fields Below Canvas */}
          <Box sx={{ mt: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Examination Methods</Typography>
              
              {/* Method Checkboxes with Text Fields */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  {/* Checkboxes in a row */}
                  <Box display="flex" alignItems="center" gap={3} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={v.methods?.direct || false}
                          onChange={(e) => onChange?.({ ...v, methods: { ...v.methods, direct: e.target.checked } })}
                        />
                      }
                      label="Direct"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={v.methods?.bio || false}
                          onChange={(e) => onChange?.({ ...v, methods: { ...v.methods, bio: e.target.checked } })}
                        />
                      }
                      label="BIO"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={v.methods?.other || false}
                          onChange={(e) => onChange?.({ ...v, methods: { ...v.methods, other: e.target.checked } })}
                        />
                      }
                      label="Other"
                    />
                  </Box>
                  
                  {/* Single text field for all methods */}
                  <TextField
                    size="small"
                    label="Method Notes"
                    placeholder="Notes for selected methods"
                    value={v.methods?.methodsText || ''}
                    onChange={(e) => onChange?.({ ...v, methods: { ...v.methods, methodsText: e.target.value } })}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
              
              {/* DFE Dropdown */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>DFE</InputLabel>
                    <Select
                      value={v.dfe || ''}
                      onChange={(e) => onChange?.({ ...v, dfe: e.target.value })}
                      label="DFE"
                    >
                      <MenuItem value="Tropicamide 0.5%">Tropicamide 0.5%</MenuItem>
                      <MenuItem value="Tropicamide 1.0%">Tropicamide 1.0%</MenuItem>
                      <MenuItem value="Cyclopentolate 1.0%">Cyclopentolate 1.0%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Time Field with Now Button */}
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    label="Time"
                    value={v.time || ''}
                    onChange={(e) => onChange?.({ ...v, time: e.target.value })}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() => {
                              const now = new Date();
                              const timeStr = now.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              });
                              onChange?.({ ...v, time: timeStr });
                            }}
                          >
                            Now
                          </Button>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
              
              {/* Declined Risks Checkbox */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={v.declinedRisks || false}
                      onChange={(e) => onChange?.({ ...v, declinedRisks: e.target.checked })}
                    />
                  }
                  label="Declined, warned of risks and symptoms"
                />
              </Box>
            </Paper>
          </Box>
          
          {/* Notes Section */}
          <Box sx={{ mt: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <TextField
                label="Notes"
                placeholder="Additional notes for internal examination"
                value={v.notes || ''}
                onChange={(e) => onChange?.({ ...v, notes: e.target.value })}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
              />
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
